import React, {useCallback, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../FaIcon';
import {MemoryConnectionsView} from './MemoryConnectionsView';
import {MemoryDocumentsView} from './MemoryDocumentsView';
import {MemoryContextPack} from './MemoryContextPack';

// "Fuentes" (ficha v2, D2): un solo lugar para ver qué alimenta la memoria del cliente. Dos
// bloques con título propio SIEMPRE visible (con ambos vacíos, dos empty-states gemelos sin
// encabezado no se distinguían — feedback fede 2026-07-06): arriba los archivos de Drive
// (conexiones vivas), abajo las reuniones/documentos ya ingeridos. La planilla del media plan
// NO aparece acá a propósito (es configuración de Líneas de Control, no memoria).
// `pollDocuments={false}` lo pasa el Recorrido, donde la lista de ingesta del acto Alimenta ya
// pollea este mismo endpoint y muestra los estados en vivo. Sin la prop, esta vista se comporta
// exactamente como siempre (es el caso del marco clásico).
// `openConnectToken` baja a las conexiones de Drive: es cómo el Recorrido abre el Picker desde un
// botón de la misma página (ver `MemoryConnectionsView`).
export function MemorySourcesView({
    clientId, clientName, refreshToken, pollDocuments = true, openConnectToken = 0,
}) {
    const {t} = useTranslation();

    // El checklist cuenta documentos POR TIPO, así que se desactualiza por dos vías distintas:
    // la ingesta (que llega de arriba como `refreshToken`) y las acciones de la tabla de abajo
    // —re-etiquetar, que es justo lo que tacha un ítem, y borrar—. Sin esto, el panel abierto
    // mostraba el conteo viejo hasta cerrarlo y volver a abrirlo.
    //
    // Las dos vías se SUMAN en el valor que baja, en vez de sincronizarse con un efecto: un
    // `useEffect(() => bumpPack(), [refreshToken])` también corre en el montaje, y como React
    // ejecuta los efectos de los hijos antes que los del padre, el checklist ya había pedido con
    // el token 0 cuando el bump lo mandaba a 1 → dos GET por apertura del panel (cuatro en dev con
    // StrictMode). Sumando no hay efecto que dispare de más.
    const [tableToken, setTableToken] = useState(0);
    const bumpPack = useCallback(() => setTableToken((v) => v + 1), []);
    const packToken = (refreshToken || 0) + tableToken;

    return (
        <div className="cmem-sources">
            {/* Contexto incompleto: strip compacto arriba de las fuentes (se auto-oculta si el
                CORE del Paquete de Contexto ya está completo). */}
            <MemoryContextPack clientId={clientId} clientName={clientName} mode="compact"
                               refreshToken={packToken}/>
            <section className="cmem-sources-block">
                {/* Sin subtítulo propio: MemoryConnectionsView ya trae su intro, que cumple ese rol. */}
                <h3 className="cmem-sources-title">
                    <i className="fas fa-folder-open"/> {t('client_memory.sources.connections_title')}
                </h3>
                <MemoryConnectionsView clientId={clientId} openConnectToken={openConnectToken}/>
            </section>
            <section className="cmem-sources-block">
                <h3 className="cmem-sources-title">
                    <i className="fas fa-handshake"/> {t('client_memory.sources.documents_title')}
                    <Link className="btn -negative cmem-sm cmem-sources-cta"
                          to={`/client/${clientId}/memory/setup`}>
                        <FaIcon iconName="plus"/> {t('client_memory.documents.upload')}
                    </Link>
                </h3>
                <p className="cmem-sources-subtitle">{t('client_memory.sources.documents_subtitle')}</p>
                {/* `refreshToken` sube con cada ingesta por la Barra Total del Recorrido, que se
                    dibuja arriba de esta misma pantalla. Se baja en vez de remontar la vista:
                    remontar tiraba el scroll, los filtros y cualquier formulario a medio llenar. */}
                <MemoryDocumentsView clientId={clientId} embedded refreshToken={refreshToken}
                                     poll={pollDocuments} onDocumentsChanged={bumpPack}/>
            </section>
        </div>
    );
}
